/* globals chrome */
import sessionActions from '../actions/SessionActions';
import sessionReader from '../store/readHelpers/SessionReader';
import apiService from './api/ApiService';
import fileDeliveryService from './FileDeliveryService';
import storeManager from '../store/StoreManager';
import questionHelperService from './questionView/QuestionHelperServices';
import presignUrlService from './PresignUrlService';
import qtiConstants from '../qti/Constants';
import utilService from './UtilService';
import dataCleanUpService from './DataCleanUpService';
import additionalResourcesService from './AdditionalResourcesService';
import clientEventType from '../constants/ClientEventType';
import SaveFileChromeHelper from './SaveFileChromeHelper';
import uiActions from '../actions/UIActions';
import stateCacheService from './StateCache';
import navigationService from './NavigationService';

let hasSentAdditionalResourcesToApp = false;

const chromeAppService = {
    /*
    ChromeOSApp is the "legacy" ChromeApp.
    The ChromeOSApp is declared initialised when we have received the init message from the App layer all the way down to
    the Player because it is only then that the Player knows which is the appId it should message back up to.
    Returns false either because this is not a ChromeOSApp (thus could be PWA app, IIB, Open Test, etc) OR because we are
    inside a ChromeOsApp but the Player has just not receive the message yet.
     */
    isInitialisedChromeOSApp() {
        const extensionIdFromChromeOSApp = sessionReader.getChromeExtensionId();
        return !!extensionIdFromChromeOSApp;
    },

    isInitMessageFromChromeOSApp(e) {
        return e.data?.command === 'init' && e.origin.indexOf('chrome') === 0;
    },

    listenToInitEventFromApp() {
        const store = storeManager.getStore();

        const eventListener = (e) => {
            const { command, extensionId } = e.data;
            // See arrow 39 in https://inspera.atlassian.net/wiki/spaces/TD/pages/4406345925#Sequence-Diagram-TT-Journey-in-ChromeApp-(ChromeOS-App)
            if (chromeAppService.isInitMessageFromChromeOSApp(e) && extensionId) {
                console.log(`Received 'init' command from ChromeOS App with extensionId/appId: ${extensionId}`);
                store.dispatch(sessionActions.setChromeExtensionId(extensionId));
                window.removeEventListener('message', eventListener);

                // Receiving this message and starting up the Test are COMPETING async flows, thus we call this function here
                // too in case the message arrived too past the Test startup. If instead the message arrived before Test startup,
                // this function will be re-invoked later once startup completes (via liveDataLoader.initServices) which contains
                // the additionalResources (GetQuestionSet) and the chromeapp type info (StartTest)
                chromeAppService.sendTestInfoToApp();
            } else {
                console.log('Unknown origin or unrecognized command:', e.origin, command, extensionId, e);
            }
        };
        window.addEventListener('message', eventListener);
    },

    // The "extensionId" is the appId of the ChromeOSApp
    postMessageToParent(command, data = {}) {
        const extensionIdFromChromeOSApp = sessionReader.getChromeExtensionId();
        if (!extensionIdFromChromeOSApp) {
            console.log(`No app/extension ID found. Cannot send '${command}' message.`, data);
            return;
        }
        chrome.runtime.sendMessage(extensionIdFromChromeOSApp, { command, ...data });
    },

    async exitChromeApp() {
        try {
            await dataCleanUpService.deleteAllDataFromAllAssessments(true);
            await apiService.notifyClientEvent(clientEventType.SEB_EXIT, '', true);
            chromeAppService.sendCloseWindowMessage();
        } catch (error) {
            console.log('Error during exit:', error);
            chromeAppService.sendCloseWindowMessage();
        }
    },

    sendCloseWindowMessage() {
        if (chromeAppService.isInitialisedChromeOSApp()) {
            chromeAppService.postMessageToParent('closeWindow');
        } else {
            stateCacheService.getStateCacheInstance().removePwaAppToken();
            navigationService.navigateToDashboardTicket(false, true);
        }
    },

    sendTestInfoToApp() {
        chromeAppService.sendTestTitle();
        chromeAppService.sendAdditionalResourcesToApp();
    },

    sendClearTestSession() {
        chromeAppService.postMessageToParent('clearTestSession');
    },

    sendSaveFileMessage(blankDelivery = false) {
        const fileContents = fileDeliveryService.getEncryptedFileContent(blankDelivery);
        const fileName = fileDeliveryService.getFileName();
        if (chromeAppService.isInitialisedChromeOSApp()) {
            chromeAppService.postMessageToParent('saveFile', { fileContents, fileName });
        } else {
            SaveFileChromeHelper.saveFileChromeHelper(fileName, fileContents);
        }
    },

    sendTestTitle() {
        const state = storeManager.getState();
        const testTitle = state.parentTest?.title || state.assessment?.title;
        chromeAppService.postMessageToParent('setTestTitle', { testTitle });
    },

    findResourcesWithUrlsToSign() {
        const state = storeManager.getState();
        const { settings, assessment, parentTest, presignedUrls } = state;
        const additionalResources = additionalResourcesService.getAdditionalResources(settings, assessment, parentTest);
        const signedUrlsNeeded = [];

        additionalResources.forEach((resource) => {
            if (resource.type === qtiConstants.additionalResourceType.FILE && resource.contentItemId) {
                const { contentItemId } = resource;
                const presignedUrlForResource = presignedUrls[contentItemId];
                const fileCiUrl = utilService.generateUrlForContentItemId(contentItemId, 'pdf', true);
                if (!presignedUrlForResource?.presignedUrl) {
                    const presignPromise = presignUrlService.loadPresignedUrl(contentItemId, fileCiUrl);
                    signedUrlsNeeded.push(presignPromise);
                }
            }
        });
        return signedUrlsNeeded;
    },
    getAdditionalResourcesWithPresignedUrls() {
        const pdfJsService = questionHelperService.getServices().pdfJsService;
        const state = storeManager.getState();
        const { settings, assessment, parentTest, presignedUrls } = state;
        const additionalResources = additionalResourcesService.getAdditionalResources(settings, assessment, parentTest);

        additionalResources.forEach((resource) => {
            const presignedUrl = presignedUrls[resource.contentItemId]?.presignedUrl;
            if (resource.type === qtiConstants.additionalResourceType.FILE && resource.contentItemId) {
                resource.pdfJsUrl = pdfJsService.getPdfJsUrl(presignedUrl);
                resource.rootUrl = qtiConstants.staticLibPaths.PDFJS;
            }
        });

        return additionalResources;
    },

    sendAdditionalResourcesToApp() {
        Promise
            .all(chromeAppService.findResourcesWithUrlsToSign())
            .then(() => {
                const additionalResources = chromeAppService.getAdditionalResourcesWithPresignedUrls();

                if (!hasSentAdditionalResourcesToApp && additionalResources.length) {
                    chromeAppService.postMessageToParent('setAdditionalResources', { additionalResources });
                    hasSentAdditionalResourcesToApp = true;
                }
            }).catch((err) => {
                window.console.error('Failed setting additional resources for chrome app', err);
            });
    },

    dispatchShowChromeAppAdditionalResourceIframe(resource) {
        const currentIframeTitle = resource.title;
        storeManager.dispatch(uiActions.uiSetCurrentIframeTitle(currentIframeTitle));
    },

    dispatchHideChromeAppAdditionalResourceIframe() {
        storeManager.dispatch(uiActions.uiSetCurrentIframeTitle(''));
    },

    getChromeAppAdditionalResourceIframeUrl(resource) {
        let url;
        if (resource.type === 'link') {
            url = resource.link;
        } else if (resource.type === 'file') {
            const pdfJsService = questionHelperService.getServices().pdfJsService;
            url = pdfJsService.getPdfJsUrl(resource.mediaInfo.presignedURL);
        }
        return url;
    },
};

export default chromeAppService;
